import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AutoComplete, Input, Spin, Typography } from 'antd'
import {
  DaDataBankSuggestion,
  findBankByBik,
  mapBankToRequisites,
  PollRequisitePatch,
  suggestBank,
} from '../dadata-party'

const { Text } = Typography

type Props = {
  value?: string
  onChange?: (value: string) => void
  onBankFill?: (patch: PollRequisitePatch) => void
}

type BankOption = { value: string; label: React.ReactNode; bank: DaDataBankSuggestion }

const SEARCH_DEBOUNCE_MS = 300

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '')
}

function isAbortError(error: unknown): boolean {
  return (error as { name?: string } | null)?.name === 'AbortError'
}

function toOption(bank: DaDataBankSuggestion): BankOption {
  const bic = bank.data?.bic || ''
  const name = bank.data?.name?.payment || bank.value
  const ks = bank.data?.correspondent_account
  return {
    value: [bic, name].filter(Boolean).join(' ') || bank.value,
    bank,
    label: (
      <div>
        <div style={{ fontWeight: 600 }}>{bic || '—'}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {[name, ks ? `к/с ${ks}` : null].filter(Boolean).join(', ')}
        </Text>
      </div>
    ),
  }
}

/**
 * БИК с подсказками DaData.
 * Запросы — только при вводе (debounce) и при выборе подсказки; на blur ничего не ищем и фокус
 * никогда не возвращаем. Каждый новый запрос отменяет предыдущий (AbortController), устаревшие
 * ответы игнорируются, поэтому вручную введённые «Название банка» / «Корр. счёт» не затираются.
 */
export const PollBikField: React.FC<Props> = ({ value = '', onChange, onBankFill }) => {
  const [options, setOptions] = useState<BankOption[]>([])
  const [loading, setLoading] = useState(false)
  const [hint, setHint] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const requestIdRef = useRef(0)
  /** БИК, по которому уже искали точное совпадение (найден или нет) — повторно не ищем */
  const lookedUpBikRef = useRef<string>('')
  const valueRef = useRef(value)
  const onBankFillRef = useRef(onBankFill)
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    valueRef.current = value
    onBankFillRef.current = onBankFill
    onChangeRef.current = onChange
  }, [value, onBankFill, onChange])

  /** Отменяет ожидающий debounce и запрос «в полёте»; спиннер гасится сразу. */
  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    abortRef.current?.abort()
    abortRef.current = null
    requestIdRef.current += 1
    setLoading(false)
  }, [])

  useEffect(() => cancelPending, [cancelPending])

  const applyBank = useCallback((bank: DaDataBankSuggestion) => {
    const patch = mapBankToRequisites(bank)
    const bik = patch['requisite.bik'] || digitsOnly(String(valueRef.current || ''))
    if (bik) {
      onChangeRef.current?.(bik)
      lookedUpBikRef.current = bik
    }
    onBankFillRef.current?.(patch)
    setHint('Банк и корр. счёт заполнятся автоматически, проверьте и при необходимости поправьте')
    setUnavailable(false)
  }, [])

  const search = useCallback(
    (query: string) => {
      cancelPending()
      const q = String(query || '').trim()
      if (q.length < 2) {
        setOptions([])
        return
      }
      const isBik = /^\d{9}$/.test(q)
      if (isBik && q === lookedUpBikRef.current) return

      timerRef.current = setTimeout(async () => {
        timerRef.current = null
        const controller = new AbortController()
        abortRef.current = controller
        const requestId = ++requestIdRef.current
        const isCurrent = () => requestId === requestIdRef.current && !controller.signal.aborted
        setLoading(true)
        try {
          if (isBik) {
            const bank = await findBankByBik(q, controller.signal)
            if (!isCurrent()) return
            lookedUpBikRef.current = q
            if (bank) {
              setOptions([toOption(bank)])
              applyBank(bank)
            } else {
              setOptions([])
              setHint('Банк не найден — заполните название вручную')
              setUnavailable(false)
            }
          } else {
            const suggestions = await suggestBank(q, 8, controller.signal)
            if (!isCurrent()) return
            setOptions(suggestions.map(toOption))
            setUnavailable(false)
          }
        } catch (error) {
          if (isAbortError(error) || !isCurrent()) return
          setOptions([])
          setUnavailable(true)
          setHint('DaData недоступен — заполните реквизиты банка вручную')
        } finally {
          if (requestId === requestIdRef.current) {
            abortRef.current = null
            setLoading(false)
          }
        }
      }, SEARCH_DEBOUNCE_MS)
    },
    [applyBank, cancelPending],
  )

  // Текст в поле: БИК (цифры) или название банка. В форму по-прежнему уходит только БИК.
  const [query, setQuery] = useState<string>(typeof value === 'string' ? value : '')
  useEffect(() => {
    const v = typeof value === 'string' ? value : ''
    // Внешнее изменение (выбор банка, загруженный ответ) — показываем БИК.
    if (v && v !== digitsOnly(query)) setQuery(v)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <div style={{ maxWidth: 400 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <AutoComplete
          value={query}
          options={options}
          style={{ width: '100%' }}
          onChange={text => {
            const raw = String(text || '')
            // Выбор подсказки из списка обрабатывает onSelect.
            if (options.some(o => o.value === raw)) return
            setHint(null)
            setUnavailable(false)
            if (/^[\d\s]*$/.test(raw)) {
              // Цифры — поиск по БИК; значение поля формы = БИК (как раньше).
              const next = digitsOnly(raw).slice(0, 9)
              setQuery(next)
              onChangeRef.current?.(next)
              if (next !== lookedUpBikRef.current) lookedUpBikRef.current = ''
              search(next)
            } else {
              // Текст — поиск по названию (DaData suggest/bank ищет и по БИК, и по названию).
              setQuery(raw)
              onChangeRef.current?.('')
              lookedUpBikRef.current = ''
              search(raw)
            }
          }}
          onSelect={(_val, option: any) => {
            if (option?.bank) {
              cancelPending()
              const bank = option.bank as DaDataBankSuggestion
              setQuery(bank.data?.bic || '')
              applyBank(bank)
            }
          }}
          notFoundContent={loading ? <Spin size="small" /> : null}
        >
          <Input
            placeholder="БИК или название банка"
            // Ушли из поля — пользователь заполняет вручную: отменяем поиск, ответы больше не применяем.
            onBlur={cancelPending}
          />
        </AutoComplete>
        {loading ? <Spin size="small" /> : null}
      </div>
      {hint ? (
        <Text
          type={unavailable ? 'warning' : 'secondary'}
          style={{ display: 'block', marginTop: 6, fontSize: 12 }}
        >
          {hint}
        </Text>
      ) : null}
    </div>
  )
}

export default PollBikField
